// v4 ticket V4-MC (spec-v4-models section 1): compare models on one scene, by tap or by voice.
//   CompareView    2-up / 2x2, each candidate lettered and labelled with its model and price. Clips play in sync:
//                  one Play plays them all from the start, muted except the focused one (tap a card to move the
//                  sound). The focused candidate's actions sit under the grid: Use this, Same model, try again
//                  (priced first), Make default (this video or the series).
//   CompareSheet   "Try on other models": frame or clip, 2-4 models (picked for the scene to start with), the total
//                  on the Start button. The tap on Start is the yes; the server refuses if it now costs more.
//   SceneCompare   the scene detail's row: the Try button, the newest comparison, and earlier tries.
// Taps tell the Director what happened ([app] user messages), so voice and touch stay in step.
import { useEffect, useMemo, useRef, useState } from "react";
import { ApiError } from "../api";
import { compareLine, latestComparison, lineupTotal, progressWords, syncPlay, toggleModel, type CompareAction } from "../compare";
import { compareApi, type AgainQuote, type CompareQuote } from "../compareClient";
import { tellDirector } from "../director";
import { bump, markActivity, toast } from "../store";
import type { CompareCandidate, Comparison, ModelInfo, Project } from "../types";
import { Icon, Sheet, Spinner } from "./bits";
import { useModels } from "./ModelPicker";
import { StillStrip, useImageShowcase } from "./ImageShowcase";
import { rowForProject, rowForTags } from "../showcaseImages";

const aspectOf = (p: Pick<Project, "brief"> | null | undefined) => ((p?.brief as { aspect?: string } | undefined)?.aspect ?? "16:9").replace(":", "x");
const errText = (e: unknown) => (e instanceof ApiError || e instanceof Error ? e.message : "That didn't work.");

// ------------------------------------------------------------------ the side-by-side view

export function CompareView({
  projectId,
  sceneId,
  sceneN,
  comparison,
  aspect = "16x9",
  compact = false,
}: {
  projectId: string;
  sceneId: string;
  sceneN: number | null;
  comparison: Comparison;
  aspect?: string;
  /** an earlier try in the scene's history: no actions bar until it's opened */
  compact?: boolean;
}) {
  const c = comparison;
  const cs = c.candidates;
  const firstReady = Math.max(0, cs.findIndex((x) => x.status === "ready"));
  const [focus, setFocus] = useState(firstReady);
  const [playing, setPlaying] = useState(false);
  const vids = useRef<(HTMLVideoElement | null)[]>([]);
  const ready = cs.filter((x) => x.status === "ready");
  const isClip = c.step === "clip";

  useEffect(() => {
    vids.current.forEach((v, i) => v && (v.muted = i !== focus));
  }, [focus]);

  const playAll = () => {
    markActivity();
    if (playing) {
      vids.current.forEach((v) => v?.pause());
      setPlaying(false);
      return;
    }
    syncPlay(vids.current, focus);
    setPlaying(true);
  };

  return (
    <section className={`cmp cmp-n${cs.length}${aspect === "9x16" ? " cmp-portrait" : ""}${compact ? " is-compact" : ""}`} aria-label={`Scene ${sceneN ?? ""} on ${cs.length} models`}>
      <div className="cmp-bar">
        <span className="cmp-progress">{progressWords(c)}</span>
        {isClip && ready.length >= 2 && (
          <button type="button" className="btn btn-primary btn-small cmp-play" onClick={playAll}>
            <Icon name={playing ? "pause" : "play"} size={16} /> {playing ? "Pause" : "Play all"}
          </button>
        )}
      </div>
      <div className="cmp-cards">
        {cs.map((x, i) => {
          const picked = c.pickedAssetId !== null && c.pickedAssetId === x.assetId;
          return (
            <figure key={x.letter} className={`cmp-card is-${x.status}${i === focus ? " is-focus" : ""}${picked ? " is-picked" : ""}`}>
              <button
                type="button"
                className={`cmp-media frame-${aspect}`}
                onClick={() => {
                  markActivity();
                  setFocus(i);
                }}
                aria-pressed={i === focus}
                aria-label={`${x.letter}: ${x.label}${isClip && x.status === "ready" ? (i === focus ? ", sound on" : ", tap for its sound") : ""}`}
              >
                {x.status === "ready" && x.url ? (
                  isClip ? (
                    <video
                      ref={(el) => {
                        vids.current[i] = el;
                      }}
                      src={x.url}
                      poster={c.fromFrameUrl ?? undefined}
                      playsInline
                      muted={i !== focus}
                      preload="metadata"
                      onEnded={() => setPlaying((p) => (vids.current.every((v) => !v || v.ended || v.paused) ? false : p))}
                    />
                  ) : (
                    <img src={x.url} alt={`${x.short}'s picture`} />
                  )
                ) : (
                  <>
                    {c.fromFrameUrl && <img className="cmp-under" src={c.fromFrameUrl} alt="" />}
                    <span className="cmp-state">{x.status === "failed" ? "Didn't work. Refunded." : <Spinner label={isClip ? "Animating" : "Drawing"} />}</span>
                  </>
                )}
                <span className="stg-letter">{x.letter}</span>
                {picked && (
                  <span className="stg-picked" aria-hidden>
                    <Icon name="check" size={18} />
                  </span>
                )}
                {isClip && x.status === "ready" && (
                  <span className="cmp-sound" aria-hidden>
                    <Icon name={i === focus ? "volume" : "mute"} size={14} />
                  </span>
                )}
              </button>
              <figcaption>
                <b>{x.short}</b>
                <span>
                  {x.credits} credits{x.durationS ? ` · ${x.durationS} s` : ""}
                </span>
              </figcaption>
            </figure>
          );
        })}
      </div>
      {!compact && cs[focus] && <CompareActions projectId={projectId} sceneId={sceneId} sceneN={sceneN} comparison={c} cand={cs[focus]!} />}
    </section>
  );
}

function CompareActions({ projectId, sceneId, sceneN, comparison, cand }: { projectId: string; sceneId: string; sceneN: number | null; comparison: Comparison; cand: CompareCandidate }) {
  const [busy, setBusy] = useState<CompareAction | null>(null);
  const [again, setAgain] = useState<AgainQuote | null>(null);
  const [scopeOpen, setScopeOpen] = useState(false);
  useEffect(() => {
    setAgain(null);
    setScopeOpen(false);
  }, [cand.letter, comparison.id]);
  const step = comparison.step;
  const inUse = comparison.pickedAssetId === cand.assetId && !!cand.assetId;
  const ok = cand.status === "ready";

  const act = async (what: CompareAction, fn: () => Promise<unknown>, done: string, scope?: "video" | "series") => {
    markActivity();
    setBusy(what);
    try {
      await fn();
      toast(done, "good");
      bump("project", "projects", "credits");
      tellDirector(compareLine(what, cand, step, sceneN, scope));
      setAgain(null);
      setScopeOpen(false);
    } catch (e) {
      toast(errText(e), "bad");
    } finally {
      setBusy(null);
    }
  };

  const use = () => act("use", () => compareApi.use(projectId, sceneId, comparison.id, cand.index), `Scene ${sceneN ?? ""} now uses ${cand.short}'s ${step}.`.replace("Scene  ", "The scene "));
  const askAgain = async () => {
    markActivity();
    setBusy("again");
    try {
      setAgain(await compareApi.againQuote(projectId, sceneId, comparison.id, cand.index));
    } catch (e) {
      toast(errText(e), "bad");
    } finally {
      setBusy(null);
    }
  };
  const doAgain = (q: AgainQuote) => act("again", () => compareApi.again(projectId, sceneId, comparison.id, cand.index, q.credits), `Making another ${step} with ${cand.short}.`);
  const makeDefault = (scope: "video" | "series") =>
    act("default", () => compareApi.makeDefault(projectId, sceneId, comparison.id, cand.index, scope), `${cand.short} is now the default for ${scope === "series" ? "the series" : "this video"}.`, scope);

  if (cand.status === "failed") return <p className="cmp-note">{cand.short} didn't work this time{cand.error ? `: ${cand.error.replace(/\.$/, "")}` : ""}. Nothing was charged for it.</p>;
  return (
    <div className="cmp-acts" aria-label={`${cand.letter}: ${cand.short}`}>
      <p className="cmp-acts-for">
        <span className="cmp-acts-letter">{cand.letter}</span> {cand.short}
        {inUse && <span className="cmp-inuse">In use</span>}
      </p>
      <div className="cmp-acts-row">
        <button type="button" className={`btn ${again ? "btn-quiet" : "btn-primary"} btn-small`} disabled={!ok || !!busy || inUse} onClick={() => void use()} data-help="compare-use">
          {busy === "use" ? "Using…" : inUse ? "Using this" : "Use this"}
        </button>
        {again ? (
          <button data-action="compare.again" type="button" className="btn btn-primary btn-small" disabled={!!busy} onClick={() => void doAgain(again)} data-autofocus>
            {busy === "again" ? "Starting…" : `Make another · ${again.credits} credits`}
          </button>
        ) : (
          <button data-action="compare.again" type="button" className="btn btn-quiet btn-small" disabled={!!busy} onClick={() => void askAgain()}>
            <Icon name="redo" size={14} /> Same model, try again
          </button>
        )}
        <button type="button" className="btn btn-quiet btn-small" disabled={!!busy} aria-expanded={scopeOpen} onClick={() => setScopeOpen(!scopeOpen)}>
          Make default
        </button>
      </div>
      {scopeOpen && (
        <div className="cmp-scope" role="group" aria-label="Make it the default for">
          <span>{step === "clip" ? "Video model" : "Picture model"} for</span>
          <button data-action="compare.default" type="button" className="btn btn-quiet btn-small" disabled={!!busy} onClick={() => void makeDefault("video")}>
            This video
          </button>
          <button data-action="compare.default" type="button" className="btn btn-quiet btn-small" disabled={!!busy} onClick={() => void makeDefault("series")}>
            The series
          </button>
        </div>
      )}
      {again && <p className="cmp-note">Another {step} with {cand.short}. It becomes the scene's new {step}, waiting for your OK.</p>}
    </div>
  );
}

// ------------------------------------------------------------------ "Try on other models"

export function CompareSheet({ p, sceneId, sceneN, initial, onClose }: { p: Project; sceneId: string; sceneN: number; initial: "frame" | "clip"; onClose: () => void }) {
  const shot = p.shots[sceneId];
  const canClip = shot?.frame?.status === "approved";
  const [step, setStep] = useState<"frame" | "clip">(initial === "clip" && canClip ? "clip" : "frame");
  const [picked, setPicked] = useState<string[] | null>(null);
  const [quote, setQuote] = useState<CompareQuote | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const models = useModels();
  const options = useMemo(() => (models ?? []).filter((m) => m.step === (step === "frame" ? "image" : "clip")), [models, step]);
  // MS-IMG: for frames, each picture model's showcase stills, the style that fits this scene first
  const stills = useImageShowcase();
  const scene = p.script?.scenes.find((s) => s.id === sceneId);
  const stillRow = rowForTags(scene?.tags ?? []) ?? rowForProject(p);

  // the suggested line-up first (no models named), then the price of whatever is picked
  useEffect(() => {
    setPicked(null);
    setQuote(null);
  }, [step]);
  useEffect(() => {
    let live = true;
    setErr(null);
    if (picked && picked.length < 2) {
      setQuote(null);
      return;
    }
    compareApi
      .quote(p.id, sceneId, { step, ...(picked ? { models: picked } : { count: 3 }) })
      .then((q) => {
        if (!live) return;
        setQuote(q);
        if (!picked) setPicked(q.candidates.map((x) => x.model));
      })
      .catch((e) => live && setErr(errText(e)));
    return () => {
      live = false;
    };
  }, [p.id, sceneId, step, JSON.stringify(picked)]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = async () => {
    if (!quote || !picked) return;
    markActivity();
    setBusy(true);
    try {
      await compareApi.start(p.id, sceneId, { step, models: picked, expectCredits: quote.total });
      bump("project", "projects", "credits");
      toast(`Trying scene ${sceneN} on ${picked.length} models.`, "good");
      tellDirector(`[app] The user started a comparison of scene ${sceneN}'s ${step} on ${quote.candidates.map((x) => x.short).join(", ")} by tapping (${quote.total} credits).`);
      onClose();
    } catch (e) {
      setErr(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const price = (m: ModelInfo) => quote?.candidates.find((x) => x.model === m.id)?.credits ?? (step === "frame" ? m.credits : m.creditsPer5s);
  return (
    <Sheet title={`Try scene ${sceneN} on other models`} onClose={onClose} className="cmp-sheet">
      <div className="sheet-body">
        <div className="cmp-steps" role="radiogroup" aria-label="What to compare">
          <button type="button" role="radio" aria-checked={step === "frame"} className={`cmp-step${step === "frame" ? " is-on" : ""}`} onClick={() => setStep("frame")}>
            <Icon name="image" size={16} /> The look <small>Frames, cheap</small>
          </button>
          <button type="button" role="radio" aria-checked={step === "clip"} disabled={!canClip} className={`cmp-step${step === "clip" ? " is-on" : ""}`} onClick={() => setStep("clip")}>
            <Icon name="film" size={16} /> The motion <small>{canClip ? "Clips from this frame" : "Approve the frame first"}</small>
          </button>
        </div>
        <p className="cmp-hint">{step === "frame" ? "Same description and reference pictures on each model." : "Every model animates the same approved frame, so the only difference is the model."} Pick 2 to 4.</p>
        {!models ? (
          <Spinner label="Loading models" />
        ) : (
          <ul className="cmp-models">
            {options.map((m) => {
              const on = !!picked?.includes(m.id);
              const why = quote?.candidates.find((x) => x.model === m.id)?.why;
              return (
                <li key={m.id}>
                  <button type="button" className={`cmp-model${on ? " is-on" : ""}`} aria-pressed={on} onClick={() => setPicked(toggleModel(picked ?? [], m.id))}>
                    <span className="cmp-check" aria-hidden>
                      {on && <Icon name="check" size={14} />}
                    </span>
                    <span className="cmp-model-name">
                      <b>{m.label}</b>
                      <small>{why ?? (m.strengths.slice(0, 2).join(", ") || " ")}</small>
                    </span>
                    <span className="cmp-model-price">{price(m) ?? "–"}</span>
                  </button>
                  {step === "frame" && stills && <StillStrip data={stills} modelId={m.id} label={m.label} row={stillRow} max={4} price={false} />}
                </li>
              );
            })}
          </ul>
        )}
        {err && (
          <p className="cmp-err" role="alert">
            {err}
          </p>
        )}
      </div>
      <footer className="sheet-foot cmp-foot">
        <span className="cmp-total">{quote ? `${quote.candidates.length} models · ${lineupTotal(quote.candidates)} credits` : picked && picked.length < 2 ? "Pick at least 2" : "Pricing…"}</span>
        <button data-action="compare.start" type="button" className="btn btn-primary" disabled={!quote || busy || (picked?.length ?? 0) < 2} onClick={() => void start()} data-autofocus>
          {busy ? "Starting…" : quote ? `Start · ${quote.total} credits` : "Start"}
        </button>
      </footer>
    </Sheet>
  );
}

// ------------------------------------------------------------------ in the scene detail

export function SceneCompare({ p, sceneId, index }: { p: Project; sceneId: string; index: number }) {
  const [open, setOpen] = useState(false);
  const [earlier, setEarlier] = useState<string | null>(null);
  const list = p.shots[sceneId]?.comparisons ?? [];
  const latest = latestComparison(p, sceneId);
  const older = list.slice(0, -1).reverse();
  const n = index + 1;
  const frameOk = p.shots[sceneId]?.frame?.status === "approved";
  return (
    <div className="detail-step cmp-step-row">
      <div className="detail-step-head">
        <span className="step-name">
          <Icon name="compare" size={16} /> Compare
        </span>
        <span className="step-acts">
          <button type="button" className="btn btn-quiet btn-small" onClick={() => setOpen(true)} data-help="compare-models">
            Try on other models
          </button>
        </span>
      </div>
      {!latest && <p className="detail-prompt">See this scene's {frameOk ? "clip" : "frame"} on two to four models side by side, then keep the one you like.</p>}
      {latest && (
        <>
          <p className="cmp-title">
            {latest.step === "clip" ? "Clips" : "Frames"} on {latest.candidates.length} models
          </p>
          <CompareView projectId={p.id} sceneId={sceneId} sceneN={n} comparison={latest} aspect={aspectOf(p)} />
        </>
      )}
      {!!older.length && (
        <details className="cmp-earlier">
          <summary>Earlier tries ({older.length})</summary>
          <ul>
            {older.map((c) => (
              <li key={c.id}>
                <button type="button" className="cmp-earlier-row" aria-expanded={earlier === c.id} onClick={() => setEarlier(earlier === c.id ? null : c.id)}>
                  {c.step === "clip" ? "Clips" : "Frames"}: {c.candidates.map((x) => x.short).join(", ")}
                  {c.pickedModel && <span className="cmp-inuse">Used {c.candidates.find((x) => x.model === c.pickedModel)?.short}</span>}
                </button>
                {earlier === c.id && <CompareView projectId={p.id} sceneId={sceneId} sceneN={n} comparison={c} aspect={aspectOf(p)} />}
              </li>
            ))}
          </ul>
        </details>
      )}
      {open && <CompareSheet p={p} sceneId={sceneId} sceneN={n} initial={frameOk ? "clip" : "frame"} onClose={() => setOpen(false)} />}
    </div>
  );
}

export { aspectOf as compareAspect };
